// pothos designer Figma-plugin: shared-pool claim
// (POST /api/claimDesignerPool, X-Designer-Plugin-Token).
// Claim = atomic participant pick (assignMerchant) + SYNCHRONOUS master
// _frame.svg render (2026-07-24: one master instead of the legacy 20-frame
// background batch — the plugin clones the master client-side, so extra
// server frames only burned the worker time budget and died mid-loop).
// Res.reason is designer-facing (plugin shows it verbatim on failure).
import { z } from 'zod';

export const PothosClaimDesignerPoolReqSchema = z.object({
  locale: z.string().trim().min(1).max(40),
  // Modern plugin sends merchant_name; `merchant` is the legacy alias the
  // handler still accepts. At least one must be non-empty (handler checks —
  // schema keeps both optional so either spelling parses).
  merchant_name: z.string().trim().max(120).default(''),
  merchant: z.string().trim().max(120).default(''),
  category: z.string().trim().max(40).default('membership'),
  country: z.string().trim().max(60).default(''),
});
export type PothosClaimDesignerPoolReq = z.infer<typeof PothosClaimDesignerPoolReqSchema>;

export const PothosClaimDesignerPoolResSchema = z.object({
  ok: z.boolean(),
  /** Present on failure — human-readable, shown in the plugin as-is. */
  reason: z.string().optional(),
  /** true on success: the master frame is rendered and pullable NOW
   *  (no more "frames not generated yet" race after claiming). */
  frame_ready: z.boolean().optional(),
  /** Instant-work (2026-07-26): успешный клейм сразу отдаёт ключ мастера —
   *  плагин открывает работу без второго запроса/клика. */
  locale: z.string().optional(),
  merchant_name: z.string().optional(),
  first_frame_r2_key: z.string().optional(),
  first_participant_id: z.string().optional(),
});
export type PothosClaimDesignerPoolRes = z.infer<typeof PothosClaimDesignerPoolResSchema>;

// ── POST /api/finalizeDesignerShipment ──────────────────────────────────
// Validate the uploaded batch (decode → value/symbology match → cross-frame
// uniqueness) → promote to shipment-ready R2 + index into pothos_assets
// (status='qa', phase 4 of pipeline v2). Response is a 3-way union:
// success / validation failure (422, errors[]) / domain error (400-409).
export const PothosFinalizeDesignerShipmentReqSchema = z.object({
  locale: z.string().trim().min(1).max(40),
  merchant_name: z.string().trim().min(1).max(120),
});
export type PothosFinalizeDesignerShipmentReq = z.infer<typeof PothosFinalizeDesignerShipmentReqSchema>;

export const PothosDesignerFrameErrorSchema = z.object({
  participant_id: z.string(),
  kind: z.string(),                        // r2_missing|no_code_decoded|value_mismatch|symbology_mismatch|duplicate_code
  detail: z.string().optional(),
  expected: z.string().optional(),
  decoded: z.array(z.string()).optional(),
  duplicate_with: z.string().optional(),
});
export type PothosDesignerFrameError = z.infer<typeof PothosDesignerFrameErrorSchema>;

export const PothosFinalizeDesignerShipmentResSchema = z.union([
  z.object({
    ok: z.literal(true),
    validated: z.number(),
    /** Кадров записано в индекс pothos_assets (status='qa'). */
    indexed: z.number().optional(),
    /** Индексация упала (finalize при этом успешен) — причина. */
    index_error: z.string().optional(),
    idempotent: z.boolean().optional(),
  }),
  z.object({ ok: z.literal(false), errors: z.array(PothosDesignerFrameErrorSchema) }),
  z.object({
    error: z.string(),
    detail: z.string().optional(),
    expected: z.number().optional(),
    actual: z.number().optional(),
    pids_missing: z.array(z.string()).optional(),
  }),
]);
export type PothosFinalizeDesignerShipmentRes = z.infer<typeof PothosFinalizeDesignerShipmentResSchema>;

// ── GET /api/getMyDesignerAssignmentExport — query contract only ─────────
// Response = полный AssignmentManifest (participants × codes с inline
// svg_base64) — велик и намеренно не контрактуется (документирован в
// figma_plugin/README.md + типах designer.ts). `style` — url-encoded JSON
// CodeStyleOverride (санитайз на воркере: shape/цвета/EC из белого списка).
export const PothosDesignerManifestQuerySchema = z.object({
  locale: z.string().min(1),
  merchant: z.string().min(1),
  style: z.string().optional(),
  seed_value: z.string().optional(),
  seed_code_type: z.string().optional(),
  seed_code_id: z.string().optional(),
  seed_linked_field_id: z.string().optional(),
  seed_linked_field_pattern: z.string().optional(),
});
export type PothosDesignerManifestQuery = z.infer<typeof PothosDesignerManifestQuerySchema>;

// ── GET /api/listMyDesignerAssignments ──────────────────────────────────
export const PothosDesignerAssignmentItemSchema = z.object({
  locale: z.string(),
  merchant_name: z.string(),
  participant_count: z.number(),
  assigned_at: z.string(),
  /** 0|1 с pipeline v2 — сервер рендерит только мастер. */
  generated_frame_count: z.number(),
  uploaded_count: z.number(),
  first_frame_r2_key: z.string(),
  first_participant_id: z.string(),
});
export const PothosDesignerAssignmentsResSchema = z.object({
  ok: z.boolean(),
  /**
   * Шапка плагина: аккаунт + счётчики ФИНАЛЬНЫХ СКРИНШОТОВ (не паков).
   * sent_total — отправлено (все финальные кадры дизайнера, растёт сразу при
   * отправке); accepted_total — принято (кадры каталога в accepted/shipped/
   * client_accepted; status='qa' ещё на проверке и не считается);
   * uploaded_total — прежний счётчик validated, оставлен для старых сборок.
   */
  account: z.object({
    email: z.string(),
    uploaded_total: z.number(),
    sent_total: z.number().optional(),
    accepted_total: z.number().optional(),
  }).optional(),
  assignments: z.array(PothosDesignerAssignmentItemSchema).default([]),
});
export type PothosDesignerAssignmentsRes = z.infer<typeof PothosDesignerAssignmentsResSchema>;
